/**
 * Client-side observability wrapper for production error capture.
 *
 * Mirrors the server wrapper interface (see `server/observability/capture.ts`).
 * No SDK is imported outside this file. The wrapper is a no-op when
 * `NEXT_PUBLIC_SENTRY_DSN` is unset (dev / test).
 *
 * The browser SDK is loaded lazily with `import("@sentry/nextjs")` the first
 * time `init()` runs with a DSN, so it never sits on a page's critical path;
 * captures that arrive before the module resolves are queued and flushed.
 *
 * Client default sample rate is 0.5 because browser errors include third-party
 * noise (extensions, ad blockers). Server / worker stay at 1.0.
 */

import type { Breadcrumb, ErrorEvent } from "@sentry/nextjs";
import { scrubBreadcrumbUrls, scrubSensitiveUrl } from "@shared/observability/scrub-url";

type SentrySdk = typeof import("@sentry/nextjs");

export interface CaptureContext {
  memorialId?: string;
  slideshowId?: string;
  orgId?: string;
  userId?: string;
  tier?: "free" | "basic" | "pro";
  route?: string;
  runtime?: "web";
  app_version?: string;
  component?: string;
  code?: string;
  fingerprint?: string[];
}

const SAFE_CONTEXT_KEYS: readonly (keyof CaptureContext)[] = [
  "memorialId",
  "slideshowId",
  "orgId",
  "userId",
  "tier",
  "route",
  "runtime",
  "app_version",
  "component",
];

const DROP_CODES = new Set(["AUTH_TOKEN_INVALID"]);

/** Bounded so a failed SDK load can never grow memory on a long-lived page. */
const MAX_PENDING = 50;

let initialized = false;
let sdk: SentrySdk | null = null;
let loading: Promise<void> | null = null;
let pending: Array<(sentry: SentrySdk) => void> = [];

export function isInitialized(): boolean {
  return initialized;
}

/**
 * Loads and initialises the SDK once a DSN is configured. Resolves immediately
 * (and loads nothing) without a DSN. Safe to call repeatedly.
 */
export function init(): Promise<void> {
  if (initialized) return Promise.resolve();
  if (loading) return loading;
  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
  if (!dsn) return Promise.resolve();

  loading = import("@sentry/nextjs")
    .then((Sentry) => {
      try {
        Sentry.init({
          dsn,
          environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ?? process.env.NODE_ENV,
          release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
          tracesSampleRate: 0,
          sampleRate: 0.5,
          sendDefaultPii: false,
          beforeSend: scrubEvent,
          beforeBreadcrumb: scrubBreadcrumb,
        });

        Sentry.setTag("runtime", "web");
        sdk = Sentry;
        initialized = true;
      } catch {
        // capture failures must never throw out of the wrapper
      }
      const queued = pending;
      pending = [];
      if (sdk) for (const run of queued) run(sdk);
    })
    .catch(() => {
      // SDK failed to load (offline, blocked): stay a no-op for this page.
      pending = [];
    });
  return loading;
}

/** Runs `fn` with the SDK now, or once it has loaded; drops it without a DSN. */
function withSdk(fn: (sentry: SentrySdk) => void): void {
  const run = (sentry: SentrySdk) => {
    try {
      fn(sentry);
    } catch {
      // never throw out of the wrapper
    }
  };
  if (sdk) {
    run(sdk);
    return;
  }
  if (!process.env.NEXT_PUBLIC_SENTRY_DSN) return;
  if (pending.length < MAX_PENDING) pending.push(run);
  void init();
}

export function captureException(error: unknown, context: CaptureContext = {}): void {
  if (context.code && DROP_CODES.has(context.code)) return;

  withSdk((Sentry) =>
    Sentry.captureException(error, {
      tags: pickTags(context),
      extra: pickExtra(context),
      fingerprint: context.fingerprint,
    })
  );
}

export function captureMessage(
  message: string,
  level: "info" | "warning" | "error" = "info",
  context: CaptureContext = {}
): void {
  if (context.code && DROP_CODES.has(context.code)) return;

  withSdk((Sentry) =>
    Sentry.captureMessage(message, {
      level,
      tags: pickTags(context),
      extra: pickExtra(context),
      fingerprint: context.fingerprint,
    })
  );
}

export function setUser(user: { id: string; email?: string } | null): void {
  withSdk((Sentry) => Sentry.setUser(user ? { id: user.id, email: user.email } : null));
}

export function setOrg(org: { id: string; slug?: string } | null): void {
  withSdk((Sentry) => {
    Sentry.setTag("orgId", org?.id ?? undefined);
    Sentry.setTag("orgSlug", org?.slug ?? undefined);
  });
}

function pickTags(context: CaptureContext): Record<string, string | number | undefined> {
  const tagKeys: (keyof CaptureContext)[] = ["route", "runtime", "tier", "app_version", "component"];
  const tags: Record<string, string | number | undefined> = {};
  for (const key of tagKeys) {
    const value = context[key];
    if (value !== undefined) tags[key] = value as string | number;
  }
  if (context.code) tags.code = context.code;
  return tags;
}

function pickExtra(context: CaptureContext): Record<string, unknown> {
  const extra: Record<string, unknown> = {};
  for (const key of SAFE_CONTEXT_KEYS) {
    if (context[key] !== undefined) extra[key] = context[key];
  }
  return extra;
}

/** Headers that never leave the browser (credentials, and the Cloudflare origin secret). */
const DROPPED_HEADERS = new Set(["authorization", "cookie", "set-cookie", "x-memoriam-origin-secret"]);

/**
 * Family links are bearer credentials and unsubscribe links carry `?token=`.
 * They appear in the page URL, the transaction name, the Referer header, the
 * query string and breadcrumbs (fetch, navigation); each is rewritten.
 */
function scrubEvent(event: ErrorEvent): ErrorEvent | null {
  if (event.request) {
    delete event.request.cookies;
    delete event.request.data;
    if (typeof event.request.url === "string") {
      event.request.url = scrubSensitiveUrl(event.request.url);
    }
    if (typeof event.request.query_string === "string") {
      event.request.query_string = scrubSensitiveUrl(event.request.query_string);
    }
    if (event.request.headers) {
      const headers: Record<string, string> = {};
      for (const [name, value] of Object.entries(event.request.headers)) {
        const lower = name.toLowerCase();
        if (DROPPED_HEADERS.has(lower)) continue;
        if (typeof value !== "string") continue;
        headers[name] = lower === "referer" ? scrubSensitiveUrl(value) : value;
      }
      event.request.headers = headers;
    }
  }

  if (typeof event.transaction === "string") {
    event.transaction = scrubSensitiveUrl(event.transaction);
  }

  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => scrubBreadcrumbUrls(breadcrumb));
  }

  return event;
}

function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  return scrubBreadcrumbUrls(breadcrumb);
}

/** Test hook — resets initialized state. Do not call from app code. */
export function __resetForTests(): void {
  initialized = false;
  loading = null;
  pending = [];
  try {
    sdk?.getCurrentScope().clear();
  } catch {
    // no-op when SDK isn't initialized
  }
  sdk = null;
}
